// Owner/Department Mapping Service
// Stores owner-to-department relationships in JSON file on SharePoint

import DEFAULT_MAPPINGS from "../data/defaultOwnerMappings.json";

export const ownerMappingService = {
  // Get all owner-department mappings
  getMappings: async (service) => {
    try {
      const drives = await service.getDrives();
      const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");
      if (!docsDrive) return DEFAULT_MAPPINGS;

      // Try to get the mapping file
      const file = await service.getFileMetadata(docsDrive.id, "owner-department-mapping.json")
        .catch(() => null);

      if (!file) {
        // Initialize with default mappings if file doesn't exist
        try {
          await ownerMappingService.saveMappings(service, DEFAULT_MAPPINGS);
          return DEFAULT_MAPPINGS;
        } catch (e) {
          console.warn("Could not initialize default mappings:", e);
          return DEFAULT_MAPPINGS;
        }
      }

      // Download and parse the JSON file
      const content = await fetch(file.webUrl)
        .then(r => r.text())
        .catch(() => JSON.stringify(DEFAULT_MAPPINGS));

      return JSON.parse(content || JSON.stringify(DEFAULT_MAPPINGS));
    } catch (e) {
      console.error("Error getting owner mappings:", e);
      return DEFAULT_MAPPINGS;
    }
  },

  // Save mappings to JSON file
  saveMappings: async (service, mappings) => {
    try {
      const drives = await service.getDrives();
      const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");
      if (!docsDrive) throw new Error("ContractDocuments drive not found");

      const jsonContent = JSON.stringify(mappings, null, 2);
      const blob = new Blob([jsonContent], { type: "application/json" });

      // Upload or replace the JSON file
      await service.uploadFile(
        docsDrive.id,
        "",
        "owner-department-mapping.json",
        blob,
        "application/json"
      );

      return true;
    } catch (e) {
      console.error("Error saving owner mappings:", e);
      throw e;
    }
  },

  // Add or update a mapping
  addOrUpdateMapping: async (service, owner, department) => {
    const mappings = await ownerMappingService.getMappings(service);
    // Remove old mapping for this department if it exists
    const filtered = mappings.filter(m => m.department !== department);
    // Add new mapping
    filtered.push({ owner, department });

    await ownerMappingService.saveMappings(service, filtered);
    return filtered;
  },

  // Delete a mapping
  deleteMapping: async (service, owner, department) => {
    const mappings = await ownerMappingService.getMappings(service);
    const filtered = mappings.filter(m => !(m.owner === owner && m.department === department));
    await ownerMappingService.saveMappings(service, filtered);
    return filtered;
  },

  // Get owner for a department
  getOwnerForDepartment: async (service, department) => {
    const mappings = await ownerMappingService.getMappings(service);
    return mappings.find(m => m.department === department)?.owner || "";
  },
};
